import { Type } from "@sinclair/typebox";
import { Account } from "./SfQueryApiResponse";
import { UserType } from "@whatslly/types/User";
import { TypeBoxToType } from "@whatslly/types/TypeBoxToType";

export const QUERY_RESPONSE_PERSON_TYPES = {
  WHATSAPP_CONTACT: "WhatsApp Contact",
  UNKNOWN: "Unknown",
  LEAD: "Lead",
  CONTACT: "Contact",
  ACCOUNT: "Account",
  USER: "User",
  Manager: "Manager",
  PERSON_ACCOUNT: "Person Account",
  BLACKLISTED: "Blacklisted",
  BLOCKED: "Blocked",
} as const;

export type QUERY_RESPONSE_PERSON_TYPES_VALUES = (typeof QUERY_RESPONSE_PERSON_TYPES)[keyof typeof QUERY_RESPONSE_PERSON_TYPES];

export const QUERY_RESPONSE_PERSON_TYPES_TYPEBOX_VALUES = Type.Union(
  Object.values(QUERY_RESPONSE_PERSON_TYPES).map((personType) => Type.Literal(personType))
);

export const enableCreateCase = (user: UserType | undefined, person: PersonType | undefined) => {
  if (!user || !person) {
    return false;
  }
  return (
    user.createCases &&
    (getPersonType(person) === QUERY_RESPONSE_PERSON_TYPES.CONTACT ||
      getPersonType(person) === QUERY_RESPONSE_PERSON_TYPES.PERSON_ACCOUNT ||
      getPersonType(person) === QUERY_RESPONSE_PERSON_TYPES.ACCOUNT)
  );
};

export const getPersonType = (person: { type: string; IsPersonAccount?: boolean } | undefined) => {
  if (person?.type === QUERY_RESPONSE_PERSON_TYPES.CONTACT) {
    return QUERY_RESPONSE_PERSON_TYPES.CONTACT;
  }
  if (person?.type === QUERY_RESPONSE_PERSON_TYPES.LEAD) {
    return QUERY_RESPONSE_PERSON_TYPES.LEAD;
  }
  if (person?.type === QUERY_RESPONSE_PERSON_TYPES.USER) {
    return QUERY_RESPONSE_PERSON_TYPES.USER;
  }
  if (person?.type === QUERY_RESPONSE_PERSON_TYPES.Manager) {
    return QUERY_RESPONSE_PERSON_TYPES.Manager;
  }
  if (person?.type === QUERY_RESPONSE_PERSON_TYPES.UNKNOWN || person?.type === QUERY_RESPONSE_PERSON_TYPES.WHATSAPP_CONTACT) {
    return QUERY_RESPONSE_PERSON_TYPES.UNKNOWN;
  }
  if (person?.type === QUERY_RESPONSE_PERSON_TYPES.BLACKLISTED) {
    return QUERY_RESPONSE_PERSON_TYPES.BLACKLISTED;
  }
  if (person?.type === QUERY_RESPONSE_PERSON_TYPES.BLOCKED) {
    return QUERY_RESPONSE_PERSON_TYPES.BLOCKED;
  }
  if (person?.type === QUERY_RESPONSE_PERSON_TYPES.ACCOUNT && !person.IsPersonAccount) {
    return QUERY_RESPONSE_PERSON_TYPES.ACCOUNT;
  }
  return QUERY_RESPONSE_PERSON_TYPES.PERSON_ACCOUNT;
};

export const Person = Type.Object(
  {
    img: Type.Optional(Type.String()),
    type: Type.String(),
    originalCaller: Type.Optional(Type.String()),
    originalPhone: Type.Optional(Type.String()),
    originalEmail: Type.Optional(Type.String()),
    Id: Type.Optional(Type.String()),
    fullname: Type.Optional(Type.String()),
    Name: Type.Optional(Type.String()),
    Title: Type.Optional(Type.String()),
    Email: Type.Optional(Type.String()),
    PersonEmail: Type.Optional(Type.String()),
    Company: Type.Optional(Type.String()),
    companyname: Type.Optional(Type.String()),
    Account: Type.Optional(Account),
    Manager: Type.Optional(Type.Record(Type.String(), Type.Union([Type.Number(), Type.String(), Type.Number()]))),
    RecordTypeId: Type.Optional(Type.String()),
    Address: Type.Optional(
      Type.Object({
        city: Type.String(),
        country: Type.String(),
        postalCode: Type.String(),
        state: Type.String(),
        street: Type.String(),
      })
    ),
    whatsllyPersonId: Type.Optional(Type.String()),
    whatslly__Whatslly_Person_Id__c: Type.Optional(Type.String()),
    emailaddress1: Type.Optional(Type.String()),
    isDefaultRecord: Type.Optional(Type.Boolean()),
    entityUrl: Type.Optional(Type.String()),
    viewInCrmUrl: Type.Optional(Type.String()),
    IsPersonAccount: Type.Optional(Type.Boolean()),
    Phone: Type.Optional(Type.String()),
    FirstName: Type.Optional(Type.String()),
    LastName: Type.Optional(Type.String()),
  },
  // Open the wire to dynamic CRM fields (custom SF `*__c`, Dynamics `new_*`, layout-driven fields).
  // The widget reads `person as Record<string, string>` and indexes by SF API name from the layout
  // — those fields must survive fast-json-stringify serialization.
  { additionalProperties: true }
);

export const PersonAttendee = Type.Object({
  id: Type.String(),
  source: Type.String(),
  name: Type.String(),
  email: Type.String(),
  type: Type.Optional(Type.String()),
  time: Type.Optional(Type.String()),
  response: Type.Optional(Type.String()),
  status: Type.Optional(Type.String()),
});

export type PersonType = TypeBoxToType<typeof Person>;
export type PersonAttendeeType = TypeBoxToType<typeof PersonAttendee>;
